import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { isModuleCalculation } from "../utils";
import {
  hydrateNestedCalculationTree,
  nestedTreeNeedsAttention,
  nestedTreeRecommendedGroupCount,
} from "../utils/nested-calculation-tree";
import "./module-builder.css";

export {
  recommendedInsertGroups,
  summarizeRecommendedInsertFields,
  recommendedFieldsNeedAttention,
} from "./nested-module-insert-fields-core";

/**
 * Compact «Anbefalte felter» entry on a nested package line.
 * Counts recommended groups across the full nested tree (not only depth 1).
 * Opens Edit calc → Produkter og felter.
 */
export default function NestedModuleInsertFields({
  nestedCalculation,
  onLocalChange,
  onOpenBuilder,
}) {
  const { t } = useTranslation();
  const [hydrated, setHydrated] = useState(nestedCalculation || null);
  const fetchedRootRef = useRef(null);

  const onLocalChangeRef = useRef(onLocalChange);
  onLocalChangeRef.current = onLocalChange;

  useEffect(() => {
    setHydrated(nestedCalculation || null);
  }, [nestedCalculation]);

  useEffect(() => {
    const id = nestedCalculation?.id;
    if (!id) return undefined;
    if (fetchedRootRef.current === id) return undefined;
    fetchedRootRef.current = id;
    let cancelled = false;
    (async () => {
      const tree = await hydrateNestedCalculationTree(
        nestedCalculation?.id
          ? { ...(nestedCalculation || {}), id }
          : nestedCalculation
      );
      if (cancelled || !tree) return;
      setHydrated(tree);
      onLocalChangeRef.current?.(tree);
    })().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [nestedCalculation?.id]);

  const groupCount = nestedTreeRecommendedGroupCount(hydrated, t);
  const needsAttention = nestedTreeNeedsAttention(hydrated, t);
  const moduleMode =
    isModuleCalculation(hydrated) ||
    groupCount > 0 ||
    (hydrated?.nested_calculations || []).length > 0 ||
    (hydrated?.items || []).some((item) => item?.nested_calculation_id);

  if (!hydrated?.id) return null;
  // Wait until we know enough to decide — avoid flashing empty after hydrate.
  if (hydrated.module_enabled == null && groupCount === 0 && !moduleMode) {
    return null;
  }
  if (!moduleMode && groupCount === 0) return null;
  if (groupCount === 0 && typeof onOpenBuilder !== "function") return null;

  const openEditCalc = () => {
    if (typeof onOpenBuilder !== "function") return;
    onOpenBuilder({ focusRecommended: true });
  };

  return (
    <div className="offer-calc-line-insert is-compact">
      <div className="offer-calc-line-insert__affordance">
        <button
          type="button"
          className={[
            "offer-calc-line-insert__trigger",
            needsAttention ? "is-warning" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={openEditCalc}
        >
          {needsAttention ? (
            <ExclamationTriangleIcon className="h-4 w-4" aria-hidden />
          ) : null}
          <span>{t("offer.calculation.line.insert_title")}</span>
          {groupCount > 0 ? (
            <span className="offer-calc-line-insert__count">{groupCount}</span>
          ) : null}
        </button>
        {needsAttention ? (
          <span className="offer-calc-line-insert__alert" role="status">
            {t("offer.calculation.line.insert_needs_attention")}
          </span>
        ) : null}
      </div>
    </div>
  );
}
